import React, { useMemo, useState, useEffect, useRef } from 'react';
import { AppointmentMeHey, getWeekDays, getDayAppointments, getDayOfWeekThaiLong } from './AppointmentMeHeyUtils';
import { isToday } from 'react-lib/utils/dateUtils';
import { convertStringToColor } from "react-lib/apps/MEH/sequence/ScheduleSetting";

interface AppointmentWeekViewMeHeyProps {
  currentDate: Date;
  appointments: AppointmentMeHey[];
  onAppointmentClick?: (appointment: AppointmentMeHey) => void;
  selectedAppointmentId?: number | null;
  onMoreClick?: (date: Date) => void;
}

interface AppointmentLayout {
  appointment: AppointmentMeHey;
  top: number;
  height: number;
  column: number;
  totalColumns: number;
}

const AppointmentWeekViewMeHey: React.FC<AppointmentWeekViewMeHeyProps> = (props) => {
  const weekDays = useMemo(
    () => getWeekDays(props.currentDate),
    [props.currentDate]
  );

  // Check if current week contains today
  const isCurrentWeek = useMemo(
    () => weekDays.some(day => isToday(day)),
    [weekDays]
  );

  const hours = Array.from({ length: 24 }, (_, i) => i);

  const getAppointmentStyle = (apt: AppointmentMeHey): { top: number; height: number } | null => {
    if (!apt.startTime || !apt.endTime) return null;

    const startHour = apt.startTime.getHours();
    const startMinute = apt.startTime.getMinutes();
    const endHour = apt.endTime.getHours();
    const endMinute = apt.endTime.getMinutes();

    // Calculate position (60px per hour)
    const top = (startHour * 60) + (startMinute);
    const endPosition = (endHour * 60) + (endMinute);
    const height = endPosition - top;

    return { top, height };
  };

  // Calculate current time indicator position
  const getCurrentTimePosition = (): number | null => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();

    // Position in pixels (60px per hour)
    return (hours * 60) + minutes;
  };

  // State for real-time current time position
  const [currentTimePosition, setCurrentTimePosition] = useState<number | null>(getCurrentTimePosition());

  // Ref to store interval ID for cleanup
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Update current time position every minute for real-time updates, synced to 0 seconds
  useEffect(() => {
    const updateTimePosition = () => {
      setCurrentTimePosition(getCurrentTimePosition());
    };

    // Update immediately
    updateTimePosition();

    // Calculate milliseconds until the next minute (when seconds = 0)
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    // Wait until the next minute, then start interval
    const timeoutId = setTimeout(() => {
      updateTimePosition(); // Update at 0 seconds

      // Then update every minute (60000ms) starting from 0 seconds
      intervalRef.current = setInterval(updateTimePosition, 60000);
    }, msUntilNextMinute);

    // Cleanup timeout and interval on unmount
    return () => {
      clearTimeout(timeoutId);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  // Detect overlapping appointments and assign columns
  const calculateAppointmentLayouts = (dayAppointments: AppointmentMeHey[]): AppointmentLayout[] => {
    const layouts: AppointmentLayout[] = [];

    // Sort appointments by start time
    const sorted = [...dayAppointments].sort((a, b) =>
      a.startTime.getTime() - b.startTime.getTime()
    );

    sorted.forEach(apt => {
      const style = getAppointmentStyle(apt);
      if (!style) return;

      // Find overlapping appointments that have already been laid out
      const overlapping = layouts.filter(layout => {
        // Check if this appointment overlaps with the current one
        const aptEnd = style.top + style.height;
        const layoutEnd = layout.top + layout.height;

        return !(aptEnd <= layout.top || style.top >= layoutEnd);
      });

      // Find the first available column
      let column = 0;
      const usedColumns = new Set(overlapping.map(o => o.column));

      while (usedColumns.has(column)) {
        column++;
      }

      // Calculate total columns needed for this group
      const maxColumn = Math.max(column, ...overlapping.map(o => o.column));
      const totalColumns = maxColumn + 1;

      // Update total columns for all overlapping appointments
      overlapping.forEach(layout => {
        if (layout.totalColumns < totalColumns) {
          layout.totalColumns = totalColumns;
        }
      });

      layouts.push({
        appointment: apt,
        top: style.top,
        height: style.height,
        column,
        totalColumns
      });
    });

    return layouts;
  };

  return (
    <div className="week-view">
      {/* Day Headers */}
      <div className="week-view-header">
        <div className="week-time-gutter"></div>
        {weekDays.map((day, index) => {
          const isTodayDate = isToday(day);
          return (
            <div key={index} className={`week-day-header ${isTodayDate ? 'is-today' : ''}`}>
              <div className="week-day-name">{getDayOfWeekThaiLong(day.getDay())}</div>
              <div className={`week-day-number ${isTodayDate ? 'is-today-number' : ''}`}>
                {day.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Time Grid - SCROLLABLE */}
      <div className="week-view-body-scrollable">
        <div className="week-view-body">
          {/* Time Column */}
          <div className="week-time-gutter-column">
            {hours.map((hour) => (
              <div key={hour} className="week-time-slot">
                <span className="week-time-label">
                  {hour.toString().padStart(2, '0')}:00
                </span>
              </div>
            ))}

            {/* Current Time Indicator in time column - only show in current week */}
            {isCurrentWeek && currentTimePosition !== null && (
              <div
                className="current-time-indicator-gutter"
                style={{ top: `${currentTimePosition}px` }}
              >
                <span className="current-time-label-gutter">
                  {new Date().getHours().toString().padStart(2, '0')}:
                  {new Date().getMinutes().toString().padStart(2, '0')}
                </span>
              </div>
            )}
          </div>

          {/* Days Container */}
          <div className="week-days-container">
            {/* Grid Lines */}
            <div className="week-grid-lines">
              {hours.map((hour) => (
                <div key={hour} className="week-grid-line"></div>
              ))}
            </div>

            {/* Day Columns */}
            {weekDays.map((day, dayIndex) => {
              const dayAppointments = getDayAppointments(props.appointments || [], day);
              const isTodayDate = isToday(day);
              const layouts = calculateAppointmentLayouts(dayAppointments);

              // Check if there are more than 10 overlapping appointments at any time
              const maxOverlap = Math.max(...layouts.map(l => l.totalColumns), 0);
              const hasMoreThanDisplay = maxOverlap > 10;

              // Find the earliest start time for overflow indicator
              const earliestOverflowTop = hasMoreThanDisplay && layouts.length > 10
                ? Math.min(...layouts.slice(10).map(l => l.top))
                : null;

              return (
                <div
                  key={dayIndex}
                  className={`week-day-column ${isTodayDate ? 'is-today-column' : ''}`}
                >
                  {/* Current Time Red Line - only for today and in current week */}
                  {isCurrentWeek && isTodayDate && currentTimePosition !== null && (
                    <div
                      className="current-time-line"
                      style={{ top: `${currentTimePosition}px` }}
                    />
                  )}

                  {/* Appointments for this day with layout */}
                  {layouts.slice(0, 10).map((layout) => {
                    const widthPercent = 100 / Math.min(layout.totalColumns, 10);
                    const leftPercent = (layout.column * widthPercent);

                    const doctorName = layout.appointment.doctor || layout.appointment.title || 'default';
                    const colors = convertStringToColor(doctorName);
                    const isSelected = props.selectedAppointmentId === layout.appointment.doctor_dsb_id;
                    console.log('layout.appointment: ', layout.appointment);
                    console.log('selectedAppointmentId: ', props.selectedAppointmentId);

                    return (
                      <div
                        key={layout.appointment.doctor_dsb_id}
                        className={`week-rounds ${isSelected ? 'selected' : ''}`}
                        style={{
                          top: `${layout.top}px`,
                          height: `${layout.height}px`,
                          left: `${leftPercent}%`,
                          width: `${widthPercent}%`,
                          backgroundColor: colors.dark,
                          color: colors.textDark,
                          border: `2px solid ${isSelected ? colors.textDark : colors.medium}`
                        }}
                        onClick={() => props.onAppointmentClick && props.onAppointmentClick(layout.appointment)}
                      >
                        <div className="appointment-time">
                          {layout.appointment.startTime.getHours().toString().padStart(2, '0')}:
                          {layout.appointment.startTime.getMinutes().toString().padStart(2, '0')}
                        </div>
                        <div className="rounds-title">{layout.appointment.title}</div>
                        {layout.appointment.location && (
                          <div className="appointment-location">{layout.appointment.location}</div>
                        )}
                      </div>
                    );
                  })}

                  {/* "+X เพิ่มเติม" indicator as floating card at earliest overflow start time */}
                  {hasMoreThanDisplay && earliestOverflowTop !== null && (
                    <div
                      className="week-more-rounds-card"
                      style={{ top: `${earliestOverflowTop}px` }}
                      onClick={(e) => {
                        e.stopPropagation();
                        props.onMoreClick && props.onMoreClick(day);
                      }}
                    >
                      +{layouts.length - 10} เพิ่มเติม
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentWeekViewMeHey;